import React from 'react';
import styles from './SiderResizeHandle.module.less';

export const SIDER_WIDTH_STORAGE_KEY = 'easy-record:sider-width:v2';
export const DEFAULT_SIDER_WIDTH = 180;
export const MIN_SIDER_WIDTH = 180;
export const MAX_SIDER_WIDTH = 360;

const RESIZE_HANDLE_HITBOX = 12;
const COLLAPSE_TRIGGER_SAFE_GAP = 12;
const COLLAPSED_SIDER_THRESHOLD = 90;

const clampSiderWidth = (width: number) =>
  Math.min(MAX_SIDER_WIDTH, Math.max(MIN_SIDER_WIDTH, width));

export const getStoredSiderWidth = () => {
  if (typeof window === 'undefined') {
    return DEFAULT_SIDER_WIDTH;
  }

  const parsed = Number(window.localStorage.getItem(SIDER_WIDTH_STORAGE_KEY));
  return Number.isFinite(parsed)
    ? clampSiderWidth(parsed)
    : DEFAULT_SIDER_WIDTH;
};

const persistSiderWidth = (width: number) => {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(
      SIDER_WIDTH_STORAGE_KEY,
      String(clampSiderWidth(width)),
    );
  }
};

type SiderResizeHandleProps = {
  enabled: boolean;
  siderWidth: number;
  onResizeWidth: (width: number) => void;
};

const SiderResizeHandle: React.FC<SiderResizeHandleProps> = ({
  enabled,
  siderWidth,
  onResizeWidth,
}) => {
  const [dragging, setDragging] = React.useState(false);
  const [boundary, setBoundary] = React.useState({ left: -1, top: 0 });
  const dragStartXRef = React.useRef(0);
  const dragStartWidthRef = React.useRef(siderWidth);
  const activePointerIdRef = React.useRef<number | null>(null);
  const rafIdRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      setBoundary({ left: -1, top: 0 });
      return;
    }

    const syncBoundary = () => {
      const sider = document.querySelector<HTMLElement>('.ant-pro-sider');
      if (!sider) return;

      const siderRect = sider.getBoundingClientRect();
      if (siderRect.width <= COLLAPSED_SIDER_THRESHOLD) {
        setBoundary({ left: -1, top: 0 });
        return;
      }

      const collapseButton = sider.querySelector<HTMLElement>(
        '.ant-pro-sider-collapsed-button',
      );
      const collapseButtonBottom =
        collapseButton?.getBoundingClientRect().bottom ?? siderRect.top;

      setBoundary({
        left: siderRect.right,
        top: Math.max(
          siderRect.top,
          collapseButtonBottom + COLLAPSE_TRIGGER_SAFE_GAP,
        ),
      });
    };

    syncBoundary();
    window.addEventListener('resize', syncBoundary);

    const sider = document.querySelector<HTMLElement>('.ant-pro-sider');
    const resizeObserver =
      sider && 'ResizeObserver' in window
        ? new ResizeObserver(syncBoundary)
        : null;
    resizeObserver?.observe(sider as HTMLElement);

    return () => {
      window.removeEventListener('resize', syncBoundary);
      resizeObserver?.disconnect();
    };
  }, [enabled, siderWidth]);

  React.useEffect(() => {
    if (!dragging) return;

    const scheduleResize = (nextWidth: number) => {
      if (rafIdRef.current !== null) {
        window.cancelAnimationFrame(rafIdRef.current);
      }
      rafIdRef.current = window.requestAnimationFrame(() => {
        rafIdRef.current = null;
        const width = clampSiderWidth(nextWidth);
        persistSiderWidth(width);
        onResizeWidth(width);
      });
    };

    const stopDragging = () => {
      activePointerIdRef.current = null;
      setDragging(false);
    };
    const handlePointerMove = (event: PointerEvent) => {
      if (
        activePointerIdRef.current !== null &&
        event.pointerId !== activePointerIdRef.current
      ) {
        return;
      }
      scheduleResize(
        dragStartWidthRef.current + event.clientX - dragStartXRef.current,
      );
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', stopDragging);
    window.addEventListener('pointercancel', stopDragging);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', stopDragging);
      window.removeEventListener('pointercancel', stopDragging);
      if (rafIdRef.current !== null) {
        window.cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [dragging, onResizeWidth]);

  if (!enabled || boundary.left <= 0) {
    return null;
  }

  return (
    <>
      {dragging && <div className={styles.dragMask} />}
      <div
        className={styles.hitBox}
        data-dragging={dragging || undefined}
        role="slider"
        tabIndex={0}
        aria-label="Resize sidebar"
        aria-orientation="vertical"
        aria-valuemin={MIN_SIDER_WIDTH}
        aria-valuemax={MAX_SIDER_WIDTH}
        aria-valuenow={Math.round(siderWidth)}
        style={
          {
            '--app-sider-boundary-x': `${boundary.left}px`,
            '--app-sider-resize-top': `${boundary.top}px`,
            '--app-sider-hitbox-left': `${boundary.left - RESIZE_HANDLE_HITBOX / 2}px`,
            '--app-sider-hitbox-width': `${RESIZE_HANDLE_HITBOX}px`,
          } as React.CSSProperties
        }
        onPointerDown={(event) => {
          if (event.button !== 0) return;
          event.preventDefault();
          event.stopPropagation();
          activePointerIdRef.current = event.pointerId;
          dragStartXRef.current = event.clientX;
          dragStartWidthRef.current = siderWidth;
          event.currentTarget.setPointerCapture?.(event.pointerId);
          setDragging(true);
        }}
        onKeyDown={(event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          const step = event.shiftKey ? 24 : 8;
          const width = clampSiderWidth(
            siderWidth + (event.key === 'ArrowRight' ? step : -step),
          );
          persistSiderWidth(width);
          onResizeWidth(width);
        }}
      />
    </>
  );
};

export default SiderResizeHandle;
